import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Avatar from '@justgiving/avatar';
import gql from 'graphql-tag';

import { StickySummaryFragment } from '../../../types/__generated__';
import { buildQuery, useBasePageQuery, usePageQuery } from '../../hooks';
import { filterByTypename } from '../../utils/graphql';
import { makePhotoUrlFromAbsoluteUrl } from '../../utils/makePhotoUrl';
import DonateButton from '../DonateButton/DonateButton';
import PageSummary from '../PageSummary/PageSummary';

import styles from './StickySummary.scss';
import { Share } from '../SocialButtons/Share';
import { MarkSuccessOnClick } from '../MarkSuccessOnClick/MarkSuccessOnClick';
import { Experiments } from '../../constants/experiments';

const FRAGMENT = buildQuery(gql`
  fragment StickySummary on Page {
    owner {
      avatar
    }
    relationships {
      campaigns(first: 1) {
        nodes {
          title
        }
      }
    }
  }
`);

const StickySummary = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const result = usePageQuery<StickySummaryFragment>(FRAGMENT);
  const basePage = useBasePageQuery();

  const basePageData = basePage.data?.page;
  const name = basePageData?.owner?.name ?? basePageData?.title ?? '';
  const beneficiaries = basePageData?.relationships?.beneficiaries;
  const charityName = beneficiaries?.nodes
    ? filterByTypename(beneficiaries.nodes, 'Charity')[0]?.name
    : null;

  const owner = result.data?.owner;
  const relationships = result.data?.relationships;

  function getAvatarImage() {
    if (owner?.avatar) {
      return (
        <Avatar
          picture={makePhotoUrlFromAbsoluteUrl(owner.avatar)}
          avatarSize="auto"
          hasBorder
        />
      );
    }

    const pageLogo = basePageData?.logo || basePageData?.branding?.logo;
    if (pageLogo) {
      return (
        <div className={`jg-bd jg-bd--very-light-grey ${styles.pageLogo}`}>
          <img
            src={makePhotoUrlFromAbsoluteUrl(pageLogo)}
            alt=""
            data-qa="stickySummary-pageLogo"
          />
        </div>
      );
    }

    return undefined;
  }

  const campaignName = relationships?.campaigns.nodes[0]
    ? relationships.campaigns.nodes[0].title
    : null;

  let subtitle;
  if (charityName && campaignName) {
    subtitle = (
      <Trans i18nKey="fundraising.subtitleCampaign">
        <span>Fundraising for </span>
        <span className="jg-text--regular">{{ campaignName }}</span>
        <span> run by </span>
        <span className="jg-text--regular">{{ charityName }}</span>
      </Trans>
    );
  } else if (charityName) {
    subtitle = (
      <>
        {t('fundraising.fundraisingFor', 'Fundraising for')}
        <span className="jg-text--regular"> {charityName}</span>
      </>
    );
  }

  return (
    <PageSummary
      image={getAvatarImage()}
      pagetitle={name}
      pagesubtitle={subtitle}
      ctas={
        <div className="row row--no-gutter jg-w-full">
          <div className="col-sm-6 jg-space-prxs">
            <MarkSuccessOnClick
              testSlug={Experiments.b2c_hide_sticky_bar}
              successType="StickyDonateButtonClick"
            >
              <DonateButton
                key="donateButton"
                trackingDataPageSection="StickySummary"
              />
            </MarkSuccessOnClick>
          </div>
          <div className="col-sm-6 jg-space-plxs">
            <Share successType="StickyShareButtonClick" />
          </div>
        </div>
      }
    />
  );
};

StickySummary.displayName = 'StickySummary';

export default StickySummary;
